import type { LoginResponse, ProblemDetails } from '@ruby/shared';
import { useSession } from './session';

const BASE = '/api/v1';

/** API error carrying RFC 9457 problem details (including field errors for forms). */
export class ApiError extends Error {
  constructor(public readonly problem: ProblemDetails) {
    super(problem.title);
  }
  get status() {
    return this.problem.status;
  }
  get code() {
    return this.problem.code;
  }
}

let refreshing: Promise<boolean> | null = null;

/**
 * Exchanges the httpOnly refresh cookie for a new access token. Single-flight
 * within a tab, and serialised across tabs with the Web Locks API so two tabs
 * never present the same rotating refresh token.
 */
export function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    const run = async () => {
      const res = await fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'same-origin' });
      if (!res.ok) return false;
      const data = (await res.json()) as LoginResponse;
      useSession.getState().setToken(data.accessToken);
      return true;
    };
    try {
      return navigator.locks ? await navigator.locks.request('rf-refresh', run) : await run();
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function api<T>(path: string, opts: RequestOptions = {}, retried = false): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = useSession.getState().accessToken;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin',
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError({
      type: 'about:blank',
      status: 0,
      title: 'Cannot reach the server. Check your connection and try again.',
      code: 'NETWORK',
    });
  }

  if (res.status === 401 && !retried && !path.startsWith('/auth/')) {
    if (await refreshAccessToken()) return api<T>(path, opts, true);
    useSession.getState().signedOut();
  }
  if (res.status === 204) return undefined as T;
  const isJson = res.headers.get('content-type')?.includes('json');
  const data = isJson ? await res.json() : null;
  if (!res.ok) {
    const problem: ProblemDetails = data ?? { type: 'about:blank', status: res.status, title: res.statusText };
    if (problem.code === 'PASSWORD_CHANGE_REQUIRED') useSession.getState().requirePasswordChange();
    throw new ApiError(problem);
  }
  return data as T;
}

export const errorMessage = (e: unknown) =>
  e instanceof ApiError ? e.message : 'Something went wrong. Please try again.';

/** Multipart upload (attachments). Same auth/refresh handling as api(). */
export async function apiUpload<T>(path: string, file: File, retried = false): Promise<T> {
  const body = new FormData();
  body.append('file', file);
  const token = useSession.getState().accessToken;
  const res = await fetch(BASE + path, { method: 'POST', body, headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'same-origin' });
  if (res.status === 401 && !retried && (await refreshAccessToken())) return apiUpload<T>(path, file, true);
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
  if (!res.ok) throw new ApiError(data ?? { type: 'about:blank', status: res.status, title: res.statusText });
  return data as T;
}

/** Fetches a protected file and opens it in a new tab (or downloads it). */
export async function openFile(path: string, fileName: string, retried = false, download = false): Promise<void> {
  const token = useSession.getState().accessToken;
  const res = await fetch(BASE + path, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'same-origin' });
  if (res.status === 401 && !retried && (await refreshAccessToken())) return openFile(path, fileName, true, download);
  if (!res.ok) throw new ApiError({ type: 'about:blank', status: res.status, title: 'Could not open the file' });
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  if (download || !/^(image\/|application\/pdf)/.test(res.headers.get('content-type') ?? '')) a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const blobCache = new Map<string, Promise<string>>();
/** A protected file (e.g. a product image) as an object URL for <img>, fetched once per session. */
export function protectedFileUrl(path: string): Promise<string> {
  let p = blobCache.get(path);
  if (!p) {
    p = (async () => {
      const token = useSession.getState().accessToken;
      const res = await fetch(BASE + path, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'same-origin' });
      if (!res.ok) throw new ApiError({ type: 'about:blank', status: res.status, title: 'Could not load the file' });
      return URL.createObjectURL(await res.blob());
    })();
    p.catch(() => blobCache.delete(path));
    blobCache.set(path, p);
  }
  return p;
}
